/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
// WATCHOUT: WE HAVE A SPECIAL LOADER FOR ENDPOINTS WHICH REMOVES THE BACKEND LOGIC. ONLY THE
// METHOD AND URI ARE INCLUDED IN THIS IMPORT
import { Capacitor } from '@capacitor/core';
import { deepEqual } from '@deckee/utilities';
import OneSignal from 'onesignal-cordova-plugin-v3';
import OneSignalPlugin from 'onesignal-cordova-plugin';

import endpointDefinitions, {
  EndpointDefinition,
} from '../../configurations/endpoints';
import { captureEventAsync } from '../event-capture';
import { clearAll, clearNonPermanent, get, set } from '../local-storage';
import { definitions, emit } from '../message-broker';
import { shouldSkipUnauthenticatedApiRedirect } from '../route/permissions';
import { getStore } from '../store';
import { logOutReset } from '../store/modules/app-controls';
import { updateCurrentMember } from '../store/modules/backend-service';
import { reset as resetOldFloatplan } from '../store/modules/float-plan';
import { reset } from '../store/modules/plan';

let telemetry: any;
let clientToken: any;
let clientTokenIsValid: any;

const generateNewToken = async () => {
  clientToken = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    /* eslint-disable-next-line no-bitwise */
    const r = (Math.random() * 16) | 0;
    /* eslint-disable-next-line no-bitwise, no-mixed-operators */
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
  await set('backendService', 'clientToken', clientToken);
};

/**
 * Detaches this device from the OneSignal user. Doing so moves the push subscription to a new
 * anonymous user, leaving the previous external ID with no subscription to deliver to, so this
 * must only run for a deliberate sign out - never for an incidental 401.
 */
export const logOutOfOneSignal = async () => {
  // Must not throw or the rest of the logout cleanup will be aborted
  try {
    if (Capacitor.isNativePlatform() && OneSignalPlugin?.logout) {
      OneSignalPlugin.logout();
    }
  } catch {
    // Silently ignore - logout cleanup must continue regardless
  }
};

export const afterLogout = async () => {
  clearAll();
  getStore().dispatch(updateCurrentMember(null));
  getStore().dispatch(logOutReset());
  getStore().dispatch(reset());
  getStore().dispatch(resetOldFloatplan());
  clientTokenIsValid = false;
  emit(definitions.authentication.stateChange, clientTokenIsValid);
  await clearNonPermanent();
  await generateNewToken();
};

export class EndpointError extends Error {
  /** The error code that the frontend can use to identify an error from the backend. e.g. "TOO_MANY_ACTIVE_FLOAT_PLANS_ERROR" */
  errorCode?: string;
  code: number;

  constructor(message: string, code: number, errorCode?: string) {
    super(message);
    this.name = 'EndpointError';
    this.code = code;
    this.errorCode = errorCode;
  }
}

interface DeckeeResponse {
  // success: boolean;
  data: any;
}

export const callEndpoint = async <Resp extends DeckeeResponse>(
  definition: EndpointDefinition,
  data: { [key: string]: any } = {},
  queryParams: { [key: string]: any } | null = null,
  signal: any = null,
): Promise<Resp['data']> => {
  if (!definition) {
    throw new Error(
      '[callEndpoint] called without a valid definition. Please check the callee of this function',
    );
  }
  const [method, uri] = definition;
  let body;
  let url = '';
  if (method.toLowerCase() === 'get') {
    if (queryParams) {
      url = `${uri}?${new URLSearchParams(queryParams)}`;
    } else if (data) {
      url = uri;
      Object.entries(data).forEach(([keyword, value]) => {
        url = `${url.replace(keyword, value)}`;
      });
    }
  } else {
    url = uri;

    const bodyData: { [key: string]: any } = {};

    if (data) {
      // For each body data item, check to see if there is a variable in the URL that needs to be replaced, otherwise include it in the body payload
      Object.entries(data).forEach(([keyword, value]) => {
        // See if the key exists in the URL, and that the key starts with a colon
        if (url.indexOf(keyword) > -1 && keyword[0] === ':') {
          url = `${url.replace(keyword, value)}`;
        } else {
          bodyData[keyword] = value;
        }
      });
    }

    body = JSON.stringify(bodyData);
  }

  const options = {
    method,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'api-key': clientToken,
    },
    body,
    signal,
  };

  const response = await fetch(url, options);

  // need to check response.ok and returned content type before calling .json()
  const wrapper = await response.json();

  // On 401 it means the user is not authenticated.
  if (response.status === 401) {
    afterLogout(); // clear client side auth details
    captureEventAsync({
      type: 'app: unauthenticated api call',
      payload: {
        source: 'call-endpoint',
        url,
      },
    }).then(() => {
      if (!shouldSkipUnauthenticatedApiRedirect(window.location.pathname)) {
        // @ts-expect-error -- TypeScript is just being precious here
        window.location = `/maps${window.location.search}${window.location.hash}`;
      }
    });
  }

  // eslint-disable-next-line no-warning-comments
  // TODO: Implement something here so that we can return to the callee in
  // a certain way if the request failed due to a 400 level error.
  if (!wrapper.success) {
    telemetry(
      telemetry.WARN,
      `[callEndpoint] unsuccessful request for ${definition}. Received ${JSON.stringify(
        wrapper,
      )}. Sent ${JSON.stringify(options)}`,
    );
    throw new EndpointError(
      `Unsuccessful request - ${wrapper?.data?.message}`,
      wrapper?.data?.code,
      wrapper?.data?.errorCode || 'GENERIC_ERROR',
    );
  }

  return wrapper.data;
};

callEndpoint.endpoints = endpointDefinitions;

export const checkClientToken = async () => {
  const member = await callEndpoint<{ data: { id: string } }>(
    callEndpoint.endpoints.checkClientToken,
  );
  // Update the returned member in the store if it has changed
  if (!deepEqual(member, getStore().getState().backendService.currentMember)) {
    getStore().dispatch(updateCurrentMember(member));
    await set('backendService', 'member', member);

    if (member) {
      const { ensureMemberSettings } =
        await import('../user/ensureMemberSettings');
      await ensureMemberSettings();
    }

    if (member && member.id && Capacitor.isNativePlatform()) {
      if (OneSignal?.setExternalUserId) {
        // Setting External User Id with Callback Available in SDK Version 2.9.0+

        try {
          OneSignal.setExternalUserId(member.id.toString(), (results: any) => {
            telemetry(
              telemetry.DEBUG,
              `[checkClientToken] Set external_user_id on OneSignal for ${
                member.id
              }, results: ${JSON.stringify(results)}`,
            );
          });
        } catch (e: any) {
          if (telemetry) {
            telemetry(
              telemetry.ERROR,
              `[checkClientToken] unable to set the external user id ${e.message}`,
            );
          }
        }
      }
    }
  }

  if ((!clientTokenIsValid && member) || (clientTokenIsValid && !member)) {
    // If there has been a change in authentication state.
    clientTokenIsValid = !!member; // we want the boolean of if member is truthy
    emit(definitions.authentication.stateChange, clientTokenIsValid);
  }
};

export const logout = async () => {
  // Detach and clear local state even when the server rejects (e.g. expired token → 401).
  // callEndpoint already runs afterLogout on 401; running it again here is idempotent enough
  // and keeps the success path ordered correctly.
  try {
    await callEndpoint(callEndpoint.endpoints.logout);
  } finally {
    await logOutOfOneSignal();
    await afterLogout();
  }
};

export const initialize = async ({
  telemetry: givenTelemetry,
}: {
  telemetry: any;
}) => {
  telemetry = givenTelemetry;

  clientToken = await get('backendService', 'clientToken');
  // If we don't have a clientToken, or the client token is malformed create a new one
  if (
    !clientToken ||
    !clientToken.match(
      /^[a-fA-F0-9]{8}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{4}-[a-fA-F0-9]{12}$/,
    )
  ) {
    await generateNewToken();
    telemetry(
      telemetry.DEBUG,
      `[callEndpoint] created new clientToken ${clientToken}`,
    );
  } else {
    telemetry(
      telemetry.DEBUG,
      `[callEndpoint] found existing clientToken ${clientToken}`,
    );
  }
};
